import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'

const buttonVariants = cva(
	'inline-flex cursor-pointer items-center justify-center gap-2 font-medium rounded-xl transition-all outline-hidden ring-offset-2 ring-offset-background focus-within:ring-2 focus-visible:ring-2 ring-indigo-500/50 disabled:pointer-events-none disabled:opacity-50',
	{
		variants: {
			variant: {
				default:
					'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-500/25',
				destructive:
					'bg-destructive text-destructive-foreground hover:bg-destructive/80',
				outline:
					'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 hover:border-slate-300',
				secondary:
					'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 hover:border-slate-300',
				ghost: 'text-slate-600 hover:text-slate-900 hover:bg-slate-100',
				link: 'text-indigo-600 underline-offset-4 hover:underline',
			},
			size: {
				default: 'px-5 py-2.5 text-sm',
				sm: 'px-4 py-2 text-sm',
				lg: 'px-6 py-3 text-base',
				xl: 'px-8 py-4 text-lg',
				wide: 'px-24 py-5',
				pill: 'px-12 py-3 leading-3',
				icon: 'size-10',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
)

export type ButtonVariant = VariantProps<typeof buttonVariants>

const Button = ({
	className,
	variant,
	size,
	asChild = false,
	...props
}: React.ComponentProps<'button'> &
	ButtonVariant & {
		asChild?: boolean
	}) => {
	const Comp = asChild ? Slot : 'button'
	return (
		<Comp
			data-slot="button"
			className={cn(buttonVariants({ variant, size, className }))}
			{...props}
		/>
	)
}

export { Button, buttonVariants }
